import React, { useEffect, useState } from "react";
import {
  Typography,
  Row,
  Col,
  Card,
  Statistic,
  Table,
  Input,
  Space,
  Tag,
  Button
} from "antd";
import {
  TeamOutlined,
  SearchOutlined,
  InstagramOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import { fetchKols } from "../store/slices/kolSlice";
import { fetchBatches } from "../store/slices/emailBatchSlice";
import { fetchCampaigns } from "../store/slices/emailCampaignSlice";
import { RootState } from "../store";
import type { ColumnsType } from "antd/es/table";
import { Kol } from "../store/slices/kolSlice";
import { useAppDispatch, useAppSelector } from "../hooks/useAppDispatch";

const { Title } = Typography;

const StyledCard = styled(Card)`
  height: 100%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border-radius: 8px;
`;

const IconWrapper = styled.div`
  font-size: 24px;
  margin-bottom: 16px;
  color: #1677ff;
`;

const Dashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const [searchText, setSearchText] = useState("");
  // 用来保存刷新时间
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // 从 Redux 中获取 kols、batches、campaigns 数据
  const { kols, isLoading: kolsLoading } = useAppSelector(
    (state: RootState) => state.kol,
  );
  const { batches } = useAppSelector((state: RootState) => state.emailBatch);
  const { campaigns } = useAppSelector(
    (state: RootState) => state.emailCampaign,
  );

  // 初始加载数据
  useEffect(() => {
    dispatch(fetchKols());
    dispatch(fetchBatches());
    dispatch(fetchCampaigns());
    // 初次加载后设置刷新时间
    setLastUpdated(new Date());
  }, [dispatch]);

  // 统计数据
  const totalKols = kols.length;
  const totalEmailBatches = batches.length;
  const totalCampaigns = campaigns.length;
  const activeCampaigns = campaigns.filter((c: any) => c.status === "active").length;

  // 在这里计算 filteredKols：在 return 之前计算好它
  const filteredKols = kols.filter(
    (kol: Kol) =>
      kol.name.toLowerCase().includes(searchText.toLowerCase()) ||
      kol.email.toLowerCase().includes(searchText.toLowerCase()) ||
      (kol.instagramHandle ? kol.instagramHandle.toLowerCase() : "").includes(searchText.toLowerCase()) ||
      (kol.tiktokHandle ? kol.tiktokHandle.toLowerCase() : "").includes(searchText.toLowerCase())
  );

  const columns: ColumnsType<Kol> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Instagram",
      dataIndex: "instagramHandle",
      key: "instagramHandle",
      render: (text) => (
        <Space>
          <InstagramOutlined style={{ color: "#E1306C" }} />
          {text || "N/A"}
        </Space>
      ),
    },
    {
      title: "Instagram Followers",
      dataIndex: "instagramFollowers",
      key: "instagramFollowers",
      sorter: (a, b) => (a.instagramFollowers || 0) - (b.instagramFollowers || 0),
      render: (followers) => new Intl.NumberFormat().format(followers || 0),
    },
    {
      title: "TikTok Followers",
      dataIndex: "tiktokFollowers",
      key: "tiktokFollowers",
      sorter: (a, b) => (a.tiktokFollowers || 0) - (b.tiktokFollowers || 0),
      render: (followers) => new Intl.NumberFormat().format(followers || 0),
    },
    {
      title: "Categories",
      dataIndex: "categories",
      key: "categories",
      render: (categories: string[]) => (
        <Space wrap>
          {(categories || []).map((category) => (
            <Tag
              color="geekblue"
              key={category}
              style={{ borderRadius: 16, padding: "2px 10px" }}
            >
              {category}
            </Tag>
          ))}
        </Space>
      ),
    },
  ];

  // 时间格式化函数，格式为 YYYY-MM-DD HH:mm
  const formatDate = (date: Date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const hh = String(date.getHours()).padStart(2, "0");
    const min = String(date.getMinutes()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
  };

  return (
    <div>
      <Title level={2}>Dashboard</Title>

      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <StyledCard>
            <IconWrapper>
              <TeamOutlined />
            </IconWrapper>
            <Statistic title="Total KOLs" value={totalKols} />
          </StyledCard>
        </Col>
      </Row>

      <Title level={4} style={{ marginTop: 24, marginBottom: 16 }}>
        KOL Overview
      </Title>

      {/* 搜索 + 刷新区域 */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <Input
          placeholder="Search KOLs"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          prefix={<SearchOutlined />}
          style={{ width: 300 }}
        />

        <div style={{ display: "flex", alignItems: "center" }}>
          <span style={{ marginRight: 8, fontSize: 14 }}>
            Last Updated Time:{" "}
            {lastUpdated ? formatDate(lastUpdated) : "N/A"}
          </span>
          <Button
            type="primary"
            onClick={() => {
              dispatch(fetchKols());
              setLastUpdated(new Date());
            }}
          >
            Refresh
          </Button>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={filteredKols}
        rowKey="id"
        loading={kolsLoading}
        pagination={{ pageSize: 10 }}
        size="middle"
        bordered={false}
        scroll={{ x: "max-content" }}
        style={{ background: "#fff", borderRadius: 8 }}
      />

    </div>
  );
};

export default Dashboard;
